import { fileNameFromValue, toArray, toRecord } from '../../../../utils/helper';
import {
  isFile,
  isGraph,
  isImageFile,
  isTable,
  ToolParam,
} from '../../../../utils/models/tool.models';

export type TimelinePreviewEntry = {
  label: string;
  value: string;
  icon?: string;
};

export function timelinePreviewEntries(
  data: Record<string, unknown> | Record<string, unknown>[] | undefined,
  parameters: ToolParam[] = []
): TimelinePreviewEntry[] {
  if (!data) return [];

  const values = toRecord(data);
  const parametersByName = new Map(parameters.map(parameter => [parameter.name, parameter]));
  return Object.entries(values).map(([name, value]) => {
    const parameter = parametersByName.get(name);
    const image = !!parameter && isImageFile(parameter);
    const icon = parameter ? previewIcon(parameter, image) : undefined;
    return {
      label: parameter?.displayName ?? name,
      value: previewEntryValue(value, parameter),
      ...(icon ? { icon } : {}),
    };
  });
}

function previewEntryValue(value: unknown, parameter?: ToolParam): string {
  if (!parameter) return previewValue(value);
  if (isGraph(parameter.param)) return 'Graph';
  if (!isFile(parameter.param)) return previewValue(value, parameter);
  if (isImageFile(parameter)) return 'Image';
  return filePreviewValue(value, parameter);
}

function previewIcon(parameter: ToolParam, image: boolean): string | undefined {
  if (isGraph(parameter.param)) return 'monitoring';
  if (isTable(parameter.param)) return 'table_chart';
  if (isFile(parameter.param)) return image ? 'image' : 'attach_file';
  return undefined;
}

function fileLabel(parameter: ToolParam): string {
  if (!isFile(parameter.param)) return 'File';
  const mediaType = parameter.param.supportedMediaTypes[0];
  if (!mediaType || mediaType === '*/*') return 'File';
  const subtype = mediaType.split('/')[1]?.replace(/^x-/, '').toUpperCase();
  return subtype ? `${subtype} file` : 'File';
}

function filePreviewValue(value: unknown, parameter: ToolParam): string {
  const fileNames = toArray(value).flatMap(file => fileNameFromValue(file) ?? []);
  return fileNames.length > 0 ? fileNames.join(', ') : fileLabel(parameter);
}

function previewValue(value: unknown, parameter?: ToolParam): string {
  if (
    Array.isArray(value) &&
    ((parameter && isTable(parameter.param)) ||
      value.every(item => item !== null && typeof item === 'object'))
  ) {
    return `${value.length} ${value.length === 1 ? 'entry' : 'entries'}`;
  }
  return valueText(value);
}

export function valueText(value: unknown): string {
  if (Array.isArray(value)) return value.map(valueText).join(', ');
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value) || '-';
  return String(value); // NOSONAR -- Object already has extra treatment here.
}
